import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { collection, query, orderBy, getDocs, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";

const NewsletterComposer = () => {
  const [blogs, setBlogs] = useState([]);
  const [selectedBlogId, setSelectedBlogId] = useState("");
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [customMessage, setCustomMessage] = useState("");
  const [recipientCount, setRecipientCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    fetchBlogs();
    fetchRecipientCount();
  }, []);

  const fetchBlogs = async () => {
    try {
      const blogsQuery = query(
        collection(db, "Blogs"),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(blogsQuery);
      const blogsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBlogs(blogsData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching blogs:", error);
      setLoading(false);
    }
  };

  const fetchRecipientCount = async () => {
    try {
      const usersQuery = query(
        collection(db, "Users"),
        where("subscribedToNewsletter", "==", true),
        where("isUnsubscribed", "==", false)
      );
      const snapshot = await getDocs(usersQuery);
      setRecipientCount(snapshot.size);
    } catch (error) {
      console.error("Error fetching recipient count:", error);
    }
  };

  useEffect(() => {
    if (selectedBlogId) {
      const blog = blogs.find(b => b.id === selectedBlogId);
      setSelectedBlog(blog);
    } else {
      setSelectedBlog(null);
    }
  }, [selectedBlogId, blogs]);

  const handleSendNewsletter = async () => {
    if (!selectedBlogId) {
      alert("Please select an article");
      return;
    }

    if (recipientCount === 0) {
      alert("No active subscribers to send to");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to send this newsletter to ${recipientCount} subscribers?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    setSending(true);
    setSendResult(null);

    try {
      const functions = getFunctions();
      const sendNewsletterEmail = httpsCallable(functions, "sendNewsletterEmail");

      const result = await sendNewsletterEmail({
        blogId: selectedBlogId,
        customMessage: customMessage.trim() || null
      });

      setSendResult({
        success: true,
        ...result.data
      });

      // Reset form
      setSelectedBlogId("");
      setCustomMessage("");
    } catch (error) {
      console.error("Error sending newsletter:", error);
      setSendResult({
        success: false,
        error: error.message || "Failed to send newsletter"
      });
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    if (!selectedBlogId) {
      alert("Please select an article first");
      return;
    }

    if (!currentUser?.email) {
      alert("No user email found");
      return;
    }

    const confirmed = window.confirm(
      `Send a test newsletter to ${currentUser.email}?`
    );

    if (!confirmed) return;

    setSending(true);

    try {
      const functions = getFunctions();
      const sendNewsletterEmail = httpsCallable(functions, "sendNewsletterEmail");

      // For test, we would need a separate function or parameter
      // For now, just show message
      alert(`Test email feature coming soon! Would send to: ${currentUser.email}`);
    } catch (error) {
      console.error("Error sending test:", error);
      alert("Failed to send test email");
    } finally {
      setSending(false);
    }
  };

  const extractExcerpt = (content, length = 200) => {
    if (!content) return "";
    const plainText = content
      .replace(/^#+ .+$/gm, '')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/\[(.+?)\]\(.+?\)/g, '$1')
      .replace(/`(.+?)`/g, '$1')
      .trim();
    return plainText.substring(0, length) + (plainText.length > length ? '...' : '');
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Newsletter Composer</h2>
        <p className="text-gray-400">Select an article and send it to your subscribers</p>
      </div>

      {/* Recipient Count Card */}
      <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div>
            <div className="text-3xl font-bold text-white">{recipientCount}</div>
            <div className="text-gray-400 text-sm">Active Subscribers</div>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-6 mb-6">
        {/* Article Selector */}
        <div className="mb-6">
          <label className="block text-white font-semibold mb-2">Select Article *</label>
          {loading ? (
            <div className="px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-gray-400">
              Loading articles...
            </div>
          ) : (
            <select
              value={selectedBlogId}
              onChange={(e) => setSelectedBlogId(e.target.value)}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 cursor-pointer"
            >
              <option value="">-- Choose an article --</option>
              {blogs.map(blog => (
                <option key={blog.id} value={blog.id}>
                  {blog.blog_title} ({new Date(blog.createdAt).toLocaleDateString()})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Custom Message */}
        <div className="mb-6">
          <label className="block text-white font-semibold mb-2">
            Custom Intro Message (Optional)
          </label>
          <textarea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Add a personal message to introduce this article to your subscribers..."
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 resize-none"
            rows={4}
            maxLength={500}
          />
          <div className="text-sm text-gray-500 mt-1">
            {customMessage.length}/500 characters
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleSendTest}
            disabled={!selectedBlogId || sending}
            className={`px-6 py-3 bg-white/5 border border-white/10 text-white rounded-lg transition-all flex items-center gap-2 ${
              !selectedBlogId || sending
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-white/10 hover:border-purple-500/50"
            }`}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Send Test
          </button>

          <button
            onClick={() => setShowPreview(!showPreview)}
            disabled={!selectedBlogId}
            className={`px-6 py-3 bg-white/5 border border-white/10 text-white rounded-lg transition-all flex items-center gap-2 ${
              !selectedBlogId
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-white/10 hover:border-purple-500/50"
            }`}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            {showPreview ? "Hide" : "Show"} Preview
          </button>

          <button
            onClick={handleSendNewsletter}
            disabled={!selectedBlogId || sending || recipientCount === 0}
            className={`flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${
              !selectedBlogId || sending || recipientCount === 0
                ? "opacity-50 cursor-not-allowed"
                : "hover:shadow-lg hover:shadow-purple-500/50"
            }`}
          >
            {sending ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Sending...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Newsletter to {recipientCount} Subscribers
              </>
            )}
          </button>
        </div>
      </div>

      {/* Preview */}
      {showPreview && selectedBlog && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 border border-white/10 rounded-lg p-6 mb-6"
        >
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Email Preview
          </h3>

          <div className="bg-[#0a0a0f] border border-white/10 rounded-lg p-6 max-w-2xl">
            {/* Header */}
            <div className="text-center mb-6 p-6 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600">
              <div className="text-2xl font-bold text-white mb-2">Cosmovex</div>
              <div className="text-white/90 text-sm">Tech Insights That Matter</div>
            </div>

            {/* Custom Message */}
            {customMessage && (
              <div className="mb-6 p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                <p className="text-purple-300 text-sm">{customMessage}</p>
              </div>
            )}

            {/* Content */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-white mb-3">{selectedBlog.blog_title}</h2>
              <p className="text-gray-400 text-sm mb-4">
                {extractExcerpt(selectedBlog.blog_contetnt, 200)}
              </p>
              <div className="inline-block px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg">
                Read Full Article →
              </div>
            </div>

            {/* Footer */}
            <div className="text-center pt-6 border-t border-white/10">
              <p className="text-gray-500 text-xs mb-2">
                You're receiving this because you subscribed to Cosmovex newsletter.
              </p>
              <p className="text-gray-500 text-xs">
                <span className="text-purple-400 underline">Unsubscribe</span> • <span className="text-purple-400">Visit Website</span>
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Send Result */}
      {sendResult && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`border rounded-lg p-6 ${
            sendResult.success
              ? "bg-green-500/10 border-green-500/20"
              : "bg-red-500/10 border-red-500/20"
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
              sendResult.success ? "bg-green-500/20" : "bg-red-500/20"
            }`}>
              {sendResult.success ? (
                <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>

            <div className="flex-1">
              <h4 className={`font-bold text-lg mb-2 ${
                sendResult.success ? "text-green-400" : "text-red-400"
              }`}>
                {sendResult.success ? "Newsletter Sent Successfully!" : "Failed to Send Newsletter"}
              </h4>

              {sendResult.success ? (
                <div className="space-y-2">
                  <p className="text-white">
                    Successfully sent to <strong>{sendResult.successCount}</strong> out of <strong>{sendResult.recipientCount}</strong> subscribers
                  </p>
                  {sendResult.failedCount > 0 && (
                    <p className="text-yellow-400 text-sm">
                      {sendResult.failedCount} emails failed to send. Check logs for details.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-red-300">{sendResult.error}</p>
              )}

              <button
                onClick={() => setSendResult(null)}
                className="mt-4 text-sm text-gray-400 hover:text-white transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default NewsletterComposer;
